/**
 * THE SIDE SHELL — every screen in the app, in a panel at one edge.
 *
 * Anthony, on staging: "move the center into a modal on the left or right
 * for now, so that I can fill the middle with something else."
 *
 * So the middle stops being where screens live. It holds the day — the
 * railway, what is on you, and a map of today's calls — and it stays on
 * screen the whole time. Everything you navigate to arrives in a panel at
 * the edge instead, over the top of the day rather than instead of it.
 *
 * ── THE THREE THINGS THAT MAKE THIS DIFFERENT FROM A DRAWER ─────────
 *
 * 1. IT IS NOT MODAL. A drawer dims the page behind it and swallows
 *    clicks, which is right when the page behind is a list you are coming
 *    back to and wrong when it is the thing you wanted to look at. The
 *    backdrop here passes every click through, so the day map stays live
 *    with a job open beside it. That is the entire point of moving the
 *    screens out of the middle; a shell that covered the middle would have
 *    moved them for nothing.
 *
 * 2. IT DOES NOT CLOSE ON A ROUTE CHANGE — it IS the route. Navigating
 *    from the jobs list to a job repaints the same panel. Closing it means
 *    going home, so the X and Escape set the hash to #/ rather than
 *    removing an element, and the router shuts the panel on its way past.
 *
 * 3. ON A WIDE SCREEN IT TAKES ITS OWN SPACE. The middle gets a margin the
 *    width of the panel, so the two sit side by side rather than one on
 *    top of the other. Under 1000px there is not room for two, so it goes
 *    back to being an overlay and the middle waits underneath.
 *
 * WHICH EDGE is the same preference the drawer uses, out of the same
 * localStorage key, changed with the same picker — two controls that mean
 * the same thing are two things to learn and two places to drift apart.
 */

import { drawerOrient, orientPickerHtml, wireOrientPicker } from "./drawer.ts";
import { esc } from "./dom.ts";

const BACK = "shell-back";
const BODY = "shell-body";
const WIDTH_KEY = "shell:w";

/**
 * How long the browser's history was when the app loaded. The back arrow
 * only steps back while there is more history than this — i.e. screens THIS
 * session opened. Opened cold on a deep link, there is nothing of ours
 * behind the panel, so back lands on the day instead of walking off the app.
 */
const START_LEN = history.length;

/** How wide the panel is, in rem, as somebody last dragged it. */
const MIN_REM = 22;
const MAX_REM = 64;
const DEFAULT_REM = 31;

function savedWidth(): number {
  try {
    const n = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(n) && n >= MIN_REM && n <= MAX_REM ? n : DEFAULT_REM;
  } catch {
    return DEFAULT_REM;
  }
}

function applyWidth(rem: number): void {
  document.documentElement.style.setProperty("--shell-w", `${rem}rem`);
}

/**
 * Open the shell, or leave it open if it already is. Called on every route
 * that is not home, so it has to be cheap and it must NOT rebuild the panel
 * — rebuilding would restart the slide-in animation on every navigation and
 * throw away the scroll position of a list you are stepping back into.
 */
export function openShell(title: string): void {
  applyWidth(savedWidth());
  const existing = document.getElementById(BACK);
  if (existing) {
    setShellTitle(title);
    return;
  }

  const orient = drawerOrient();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back is-drawer is-shell" id="${BACK}" data-orient="${orient}">
      <section class="sheet drawer-panel shell-panel" aria-label="${esc(title)}">
        <div class="shell-grab" id="shell-grab" role="separator"
             aria-label="Drag to make this panel wider or narrower"></div>
        <div class="drawer-bar shell-bar">
          <button class="shell-home" id="shell-home" type="button"
                  title="Go back">&larr; <span>Back</span></button>
          <h2 class="shell-title" id="shell-title">${esc(title)}</h2>
          ${orientPickerHtml()}
          <button class="sheet-x" id="shell-x" aria-label="Close and go back to the day">&times;</button>
        </div>
        <div class="sheet-body shell-body" id="${BODY}"></div>
      </section>
    </div>`);

  const back = document.getElementById(BACK);
  if (!back) return;
  document.documentElement.setAttribute("data-shell", orient);

  wireOrientPicker(back);
  const home = () => { location.hash = "#/"; };
  /**
   * THE ARROW STEPS BACK, THE X CLOSES.
   *
   * Anthony: "back should take you to the previous tile you were on as you
   * move forward and back, not close and put you back to home." The panel
   * IS the route, so the browser's own history is the trail of screens you
   * opened — a job, the jobs list, the day. The back arrow walks it; each
   * step repaints the panel to the screen before, and the step that reaches
   * the day closes the panel on its way past. The X still goes straight
   * home. If nothing of ours is behind the panel (a cold deep-link), back
   * lands on the day rather than walking off the app.
   */
  const goBack = () => { if (history.length > START_LEN) history.back(); else home(); };
  document.getElementById("shell-x")?.addEventListener("click", home);
  document.getElementById("shell-home")?.addEventListener("click", goBack);
  wireGrab();

  /**
   * ESCAPE GOES HOME, BUT ONLY IF NOTHING ELSE IS LISTENING.
   *
   * A sheet or a modal drawer opened from inside a screen is on top of this
   * one and has its own Escape handler. Both would fire, so one key press
   * would shut the sheet AND navigate away from the screen behind it,
   * which reads as the app losing your place. If anything is stacked above,
   * this one stands down and lets the top thing have the key.
   */
  const onKey = (e: KeyboardEvent) => {
    if (e.key !== "Escape") return;
    if (document.querySelector(".sheet-back:not(.is-shell)")) return;
    home();
  };
  document.addEventListener("keydown", onKey);
  shellKeyOff = () => document.removeEventListener("keydown", onKey);
}

let shellKeyOff: (() => void) | null = null;

function setShellTitle(title: string): void {
  const h = document.getElementById("shell-title");
  if (h) h.textContent = title;
  document.getElementById(BACK)?.querySelector(".shell-panel")
    ?.setAttribute("aria-label", title);
}

/** Shut the panel. The router does this when the hash comes back to #/. */
export function closeShell(): void {
  shellKeyOff?.();
  shellKeyOff = null;
  document.getElementById(BACK)?.remove();
  document.documentElement.removeAttribute("data-shell");
}

/**
 * DRAGGING THE INNER EDGE.
 *
 * The panel is now the whole app rather than a peek at one record, and how
 * wide the whole app should be is not a decision anybody can make for
 * somebody else's screen. A pay run wants it wide; a job beside the map
 * wants it narrow.
 *
 * Pointer events rather than mouse events, so a finger on the shop tablet
 * drags it too. setPointerCapture is what keeps the drag alive when the
 * pointer outruns the 6px grabber, which it always does.
 *
 * Top and bottom have no vertical drag: they are short sheets by design and
 * a draggable height there is a control nobody asked for. The grabber hides
 * itself on those edges in CSS.
 */
function wireGrab(): void {
  const grab = document.getElementById("shell-grab");
  if (!grab) return;
  let startX = 0;
  let startRem = DEFAULT_REM;
  const rootFont = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  grab.addEventListener("pointerdown", (e) => {
    const o = document.getElementById(BACK)?.getAttribute("data-orient");
    if (o !== "left" && o !== "right") return;
    startX = e.clientX;
    startRem = savedWidth();
    grab.setPointerCapture(e.pointerId);
    document.body.classList.add("shell-dragging");

    const move = (m: PointerEvent) => {
      // Widening means dragging INWARD, which is leftward on a right-hand
      // panel and rightward on a left-hand one. Same gesture, mirrored.
      const dx = (m.clientX - startX) / rootFont();
      const rem = Math.min(MAX_REM, Math.max(MIN_REM, startRem + (o === "right" ? -dx : dx)));
      applyWidth(rem);
      try { localStorage.setItem(WIDTH_KEY, String(Math.round(rem * 10) / 10)); } catch { /* private window */ }
    };
    const up = () => {
      grab.removeEventListener("pointermove", move);
      grab.removeEventListener("pointerup", up);
      grab.removeEventListener("pointercancel", up);
      document.body.classList.remove("shell-dragging");
    };
    grab.addEventListener("pointermove", move);
    grab.addEventListener("pointerup", up);
    grab.addEventListener("pointercancel", up);
  });

  // A double click puts it back, because a panel dragged to 22rem by
  // accident is otherwise a fiddly thing to recover.
  grab.addEventListener("dblclick", () => {
    applyWidth(DEFAULT_REM);
    try { localStorage.setItem(WIDTH_KEY, String(DEFAULT_REM)); } catch { /* private window */ }
  });
}
